import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * MUI Components
 */
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';

const WorkServiceControl = ({ value, list, handleChange, activityFilter = true }) => {
    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState(value);
    const [selectedActivity, setSelectedActivity] = useState('ALL');

    // Extraer lista única de actividades
    const activityOptions = useMemo(() => {
        const activityMap = new Map();
        list.forEach(service => {
            (service.activities || []).forEach(activity => {
                if (activity?.id && activity?.name && !activityMap.has(activity.id)) {
                    activityMap.set(activity.id, activity);
                }
            });
        });
        return Array.from(activityMap.values());
    }, [list]);

    const handleLocalChange = (event) => {
        const newValue = event.target.value;
        setLocalValue(newValue);
        handleChange(event);
    };

    const handleActivityChange = (event) => {
        setSelectedActivity(event.target.value);
    };

    // Filtrar lista según actividad
    const filteredList = useMemo(() => {
        if (!activityFilter) return list;
        if (selectedActivity === 'ALL') return list;
        if (selectedActivity === 'NO_ACTIVITY') {
            return list.filter(service => !service.activities || service.activities.length === 0);
        }
        return list.filter(service =>
            service.activities?.some(a => a.id === selectedActivity)
        );
    }, [list, selectedActivity, activityFilter]);

    // Sincroniza localValue al cargar
    useEffect(() => {
        setLocalValue(value);
        handleChange({ target: { name: 'selectService', value } });
    }, [value]);

    return (
        <Box>
            {activityFilter && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="select-activity-label">{t('Task activity')}</InputLabel>
                    <Select
                        labelId="select-activity-label"
                        id="select-activity"
                        value={selectedActivity}
                        onChange={handleActivityChange}
                        label={t('Task activity')}
                    >
                        <MenuItem value="ALL">{t('All activities')}</MenuItem>
                        <MenuItem value="NO_ACTIVITY">{t('Without activity')}</MenuItem>
                        {activityOptions.map(activity => (
                            <MenuItem key={activity.id} value={activity.id}>
                                {activity.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <FormControl fullWidth>
                <InputLabel id="select-service-label">{t('Work services')}</InputLabel>
                <Select
                    labelId="select-service-label"
                    id="select-service"
                    name="selectService"
                    value={localValue}
                    onChange={handleLocalChange}
                    label={t('Work services')}
                    required
                    disabled={filteredList.length === 0}
                >
                    <MenuItem key={0} value={0}>
                        {t('No task')}
                    </MenuItem>
                    {filteredList.map(service => (
                        <MenuItem key={service.id} value={service.id}>
                            {service.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

WorkServiceControl.propTypes = {
    value: PropTypes.number,
    list: PropTypes.array.isRequired,
    handleChange: PropTypes.func.isRequired,
    activityFilter: PropTypes.bool,
};

export default WorkServiceControl;
